import { Divider, Headline, Text, TextLink } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { RegisterOrUpdateConsumptionFootnoteForDialog } from '~/app/compositions/content/calculation-info/components/consumption-footnote';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogProps } from '~/app/pages/summary/dialogs/dialog-props';
import { RateSectionContainerStyles } from '~/app/pages/summary/dialogs/price-detail/core/styles/price-details-style';
import { Dialog } from '~/components/dialog';
import { useIframeDialog } from '~/components/dialog/iframe-dialog/iframe-dialog';
import { useFootnotes } from '~/components/footnotes';
import { CAE_CONFIGUREDCAR_REMARK } from '~/components/footnotes/constants';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';
import { RenderHtml } from '~/components/text';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCarlineQuery } from '~/graphql/hooks/use-configured-car-queries';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';
import { state } from '~/storage';
import { PriceSection } from '../components/price-section';
import { RateSection } from '../components/rate-section';

export function PriceDetailsLegalNetherlandsDialog({ open, onClose }: DialogProps) {
	return (
		<Dialog open={open} onCloseButtonClick={onClose}>
			<PriceDetailsDialogContent />
		</Dialog>
	);
}

function PriceDetailsDialogContent() {
	const { catalog, carIdentifier } = useConfigurator();
	const { config } = useMarketConfig();
	const { i18n, i18nKeys } = useI18n();
	const { formatCurrency, formatMonthlyRate } = useCurrencyFormatter();
	const vatConfig = useVATConfig();
	const { priceSummary } = useCatalog();
	const { openDialog, closeDialog } = useDialogs();
	const financialProducts = useAtomValue(state.financialProducts);
	const selectedProductSettings = useAtomValue(state.fsagFinancialCalculatorSettings);
	const { priceDateRemark } = usePriceDetailRemarks(config.useStaticPriceDateRemark);
	const { getReference } = useFootnotes();
	let product: string;
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const { showIframeDialog } = useIframeDialog();
	const carlineData = useCarlineQuery(carIdentifier, config.financingInput);

	if (config.showCaESummaryText) {
		RegisterOrUpdateConsumptionFootnoteForDialog();
	}

	// price order according to the market, total price is rotr.
	const priceOrder = ['model', 'options', 'vat', 'divider', 'total', 'rotrRate', 'divider', 'rotr'];

	const prices = priceSummary?.parts || [];

	if (selectedProductSettings) {
		product = selectedProductSettings['@ID'] ?? '';
	} else {
		product = defaultProduct ?? '';
	}

	const carType =
		carlineData?.carline?.configuredCar?.carlineStructureCarline?.vehicleType ||
		carlineData?.carline?.configuredCar?.technicalDataV2?.modelAttributes?.driveTrain;
	const priceDisclaimerText = useMemo(() => {
		if (config.enablePriceDisclaimer) {
			return carType
				? carType === 'BEV'
					? i18n('ngc.remarks.price.legalBEV')
					: i18n('ngc.remarks.price.legalICE')
				: undefined;
		}
		return null;
	}, [config.enablePriceDisclaimer, carType, i18n]);

	return (
		<div className="flex flex-col gap-8">
			<div className="flex flex-col gap-2">
				<Headline tag="h2" variant={{ type: 'headline', order: '1', weight: 'bold' }}>
					{catalog.carInfo?.carline}
				</Headline>
				<Headline tag="h3" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{catalog.carInfo?.model}
					&nbsp;
					<FootnoteReferenceNgc reference={getReference(CAE_CONFIGUREDCAR_REMARK)} />
				</Headline>
			</div>

			<div className="flex flex-col">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{i18n('ngc.priceDetails.title')}</Text>
			</div>

			{/* Price detail including total price */}
			<ul className="flex flex-col gap-6">
				{priceOrder.map((price) => {
					return price === 'divider' ? (
						<Divider key={price} />
					) : (
						<PriceSection
							key={price}
							pricePartType={price}
							title={i18n(`ngc.priceDetails.${price}` as keyof typeof i18nKeys)}
							price={formatCurrency(
								prices.find((part) => part.type === price)?.price,
								vatConfig.priceParts[price],
							)}
							disclaimer={price === 'rotr' ? priceDisclaimerText : null}
							bold={price === 'rotr'}
						/>
					);
				})}
			</ul>

			{/* Leasing price */}
			{catalog?.priceSummary?.monthlyRate?.__typename === 'MonthlyRate' && config.enableFinancingServices && (
				<RateSectionContainerStyles>
					<RateSection
						pricePartType="totalRate"
						title={
							financialProducts.find((prod) => {
								return prod.key === product;
							})?.val ?? i18n('ngc.priceDetails.privateLeasing')
						}
						price={formatMonthlyRate(catalog.priceSummary?.monthlyRate)}
						bold
					/>
					{}

					<TextLink
						onClick={() => {
							closeDialog('priceDetails');
							openDialog('monthlyPriceRatesFsag');
						}}
						size="sm"
						iconPosition={'trailing'}
						icon={'forward'}
						variant="standalone">
						{i18n('ngc.actions.changeLeasingRate')}
					</TextLink>
					{config.deliveryCostUrl && (
						<TextLink
							onClick={
								config.useIframe
									? () =>
											showIframeDialog({
												url: config.deliveryCostUrl,
												name: 'delivery-cost-layer',
											})
									: () => window.open(config.specialOfferUrl)
							}
							size="sm"
							iconPosition={'trailing'}
							icon={'forward'}
							variant="standalone">
							{i18n('ngc.priceDetails.deliveryCosts')}
						</TextLink>
					)}
				</RateSectionContainerStyles>
			)}
			{priceDateRemark && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
					<RenderHtml html={priceDateRemark} />
				</Text>
			)}
		</div>
	);
}

/**
 * Returns the remark text to be shown in the bottom of the price details layer.
 */
function usePriceDetailRemarks(staticRemark: boolean): {
	priceDateRemark: string | undefined | null;
} {
	const { i18n } = useI18n();
	const { priceSummary } = useCatalog();
	return {
		priceDateRemark: staticRemark ? i18n('ngc.summary.remark') : priceSummary?.priceDate?.text,
	};
}
